import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { FaLinkedinIn, FaGithub } from "react-icons/fa";
import { FiMail, FiFileText } from "react-icons/fi";

import { connectLinks } from "../../data/constants";
import {
  fadeUp,
  revealLine,
  stampIn,
  staggerContainer,
  viewport,
} from "../../utils/motion";
import {
  SectionWrap,
  SectionInner,
  SectionHead,
  Eyebrow,
  SectionTitle,
  SectionDesc,
  MaskLine,
} from "../common/Section";

// Connect / "Let's Connect". The objective's five white cards on a pale green
// field. Its pill CTAs cannot survive the design contract, so they render as
// square tinted buttons — which is dell-1996's sticker vocabulary anyway.
//
// This replaces the previous EmailJS contact form: the objective has no form,
// so email is a mailto: card instead.

const Row = styled(motion.div)`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 460px) {
    grid-template-columns: 1fr;
  }
`;

// Hover deepens the bevel, so the card reads as lifting off the page — the
// dell-1996 sticker "pinned on" effect, made interactive.
const Card = styled(motion.a)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 9px;
  padding: 26px 16px 20px;
  text-decoration: none;

  background: ${({ theme }) => theme.biome.connect.card};
  border: 1px solid rgba(0, 0, 0, 0.28);
  border-radius: 0;
  box-shadow: ${({ theme }) => theme.bevelSm} ${({ theme }) => theme.frameInk};
  transition: transform 120ms linear, box-shadow 120ms linear;

  &:hover {
    transform: translate(-3px, -3px);
    box-shadow: 6px 6px 0 ${({ theme }) => theme.frameInk};
  }
`;

const Glyph = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 30px;
  line-height: 1;
  color: ${({ $tint }) => $tint};
`;

const Title = styled.span`
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: 17px;
  font-weight: 900;
  color: ${({ theme }) => theme.biome.connect.ink};
`;

const Sub = styled.span`
  font-family: ${({ theme }) => theme.fonts.body};
  font-size: 12.5px;
  line-height: 1.35;
  color: ${({ theme }) => theme.biome.connect.muted};
`;

const Cta = styled.span`
  margin-top: 6px;
  padding: 7px 12px;

  font-family: ${({ theme }) => theme.fonts.ui};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.4px;
  text-transform: uppercase;

  color: ${({ $tint }) => $tint};
  background: transparent;
  border: 1px solid ${({ $tint }) => $tint};
  border-radius: 0;
`;

const ICONS = {
  resume: FiFileText,
  linkedin: FaLinkedinIn,
  email: FiMail,
  github: FaGithub,
};

const Contact = () => (
  <SectionWrap id="connect" tone="connect">
    <SectionInner>
      <SectionHead
        variants={staggerContainer(0.08)}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
      >
        <Eyebrow tone="connect" variants={fadeUp}>
          Treasure found
        </Eyebrow>
        <MaskLine>
          <SectionTitle tone="connect" variants={revealLine}>
            Let's Connect
          </SectionTitle>
        </MaskLine>
        <SectionDesc tone="connect" variants={fadeUp}>
          Take what you need, adventurer.
        </SectionDesc>
      </SectionHead>

      <Row
        variants={staggerContainer(0.06)}
        initial="hidden"
        whileInView="show"
        viewport={viewport}
      >
        {connectLinks.map((link) => {
          const Icon = ICONS[link.icon];
          const external = !link.href.startsWith("mailto:");

          return (
            <Card
              key={link.id}
              variants={stampIn}
              href={link.href}
              target={external ? "_blank" : undefined}
              rel={external ? "noreferrer" : undefined}
            >
              <Glyph $tint={link.tint}>{Icon && <Icon />}</Glyph>
              <Title>{link.title}</Title>
              <Sub>{link.subtitle}</Sub>
              <Cta $tint={link.tint}>{link.cta} &rarr;</Cta>
            </Card>
          );
        })}
      </Row>
    </SectionInner>
  </SectionWrap>
);

export default Contact;
